import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';

export type DealiodeskLogoVariant = 'full' | 'icon' | 'wordmark';
export type DealiodeskLogoTheme = 'light' | 'dark';

/**
 * dealiodesk brand logo.
 *
 * Renders the full lockup, the icon only, or the wordmark only, with an
 * optional playful hover animation.
 *
 * IMPORTANT IMPLEMENTATION NOTES:
 *
 * 1. The SVG is rendered INLINE in the template (not via [innerHTML] /
 *    DomSanitizer). Inline template SVG gets Angular's encapsulation
 *    attributes applied to all children, which is required for the
 *    hover rules scoped by `.dealiodesk-logo .dd-*` to match.
 *
 * 2. `encapsulation: ViewEncapsulation.None` is used so that our CSS can
 *    beat the global `svg { pointer-events: none; }` rule in styles.scss
 *    without fighting encapsulation attribute specificity. All selectors
 *    are scoped via the unique `.dealiodesk-logo` / `.dd-*` class names,
 *    so no leakage.
 *
 * @example Full lockup, fixed height
 *   <app-dealiodesk-logo [height]="44" />
 *
 * @example Icon variant, non-animated, as a clickable home link
 *   <app-dealiodesk-logo
 *     variant="icon"
 *     [height]="32"
 *     [animated]="false"
 *     [clickable]="true"
 *     (logoClick)="goHome()"
 *   />
 *
 * @example Dark background, wordmark only
 *   <app-dealiodesk-logo variant="wordmark" theme="dark" [height]="24" />
 */
@Component({
  selector: 'app-dealiodesk-logo',
  standalone: true,
  imports: [],
  templateUrl: './dealiodesk-logo.component.html',
  styleUrl: './dealiodesk-logo.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class DealiodeskLogoComponent {
  /** Which part of the lockup to render. Defaults to the full horizontal lockup. */
  @Input() public variant: DealiodeskLogoVariant = 'full';

  /** Light (default) or dark — adjusts wordmark color for use on dark backgrounds. */
  @Input() public theme: DealiodeskLogoTheme = 'light';

  /**
   * Fixed height in px. If provided, the logo renders at this exact height
   * (width auto-computes from the variant's aspect ratio).
   * If null, the logo fills its container's width.
   */
  @Input() public height: number | null = null;

  /** Whether to play the hover animation. */
  @Input() public animated: boolean = true;

  /**
   * Whether the logo should look & behave clickable (cursor, keyboard focus).
   * When true, pressing the logo emits `logoClick`.
   */
  @Input() public clickable: boolean = false;

  /** Accessible label. Override when the logo is used as a button. */
  @Input() public ariaLabel: string = 'dealiodesk';

  /** Fired when the logo is clicked or activated via keyboard. Only relevant when `clickable` is true. */
  @Output() public logoClick: EventEmitter<void> = new EventEmitter<void>();

  /** viewBox for the chosen variant — each variant has its own tightly-cropped coordinate system. */
  public get viewBox(): string {
    switch (this.variant) {
      case 'icon':     return '0 0 100 100';
      case 'wordmark': return '0 0 198 52';
      case 'full':
      default:         return '0 0 270 100';
    }
  }

  /** Natural aspect ratio for each variant, used to derive width from height. */
  public get aspectRatio(): number {
    switch (this.variant) {
      case 'icon':     return 1;         // 100 / 100
      case 'wordmark': return 198 / 52;  // ~3.81
      case 'full':
      default:         return 2.7;       // 270 / 100
    }
  }

  public get computedWidth(): number | null {
    return this.height === null ? null : Math.round(this.height * this.aspectRatio);
  }

  public onActivate(event: MouseEvent | KeyboardEvent): void {
    if (!this.clickable) return;
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }
    this.logoClick.emit();
  }
}
